<script>
  // Watches the deployed build and offers a reload when it changes.
  //
  // A single-page app keeps running whatever bundle it booted with, so after a
  // deploy the tab can sit on old code for days -- which is exactly how an
  // already-fixed bug appears to still be there. /health is unauthenticated and
  // returns the running version, so polling it is cheap and works whether or
  // not the session is still valid.
  //
  // index.html is served no-cache and the assets are content-hashed, so an
  // ordinary reload is enough to pick the new build up; nothing needs purging.
  import { onMount } from 'svelte';

  const POLL_MS = 60_000;

  let booted = null;          // version this tab started on
  let seen = '';              // a different version read once, not confirmed yet
  let latest = $state('');    // newer version seen on the server
  let dismissed = $state(false);

  const show = $derived(!!latest && !dismissed);

  async function readVersion() {
    try {
      const r = await fetch('/health', { cache: 'no-store' });
      if (!r.ok) return '';
      const body = await r.json();
      return body?.version || '';
    } catch {
      return ''; // offline or mid-deploy: try again on the next tick
    }
  }

  onMount(() => {
    let timer = null;
    let stopped = false;

    async function check() {
      // Nothing to compare against yet, or the tab is in the background and
      // the answer can wait until it is looked at again.
      if (stopped || document.hidden) return;
      const version = await readVersion();
      if (stopped || !version) return;
      if (!booted) {
        booted = version;
        return;
      }
      if (version === booted) {
        // Back on the version this tab is already running: either the odd
        // reading below was the rollout, or a deploy was rolled back.
        seen = '';
        latest = '';
        return;
      }
      // Mid-rollout both revisions answer for a while, so readings alternate
      // between the old and the new build. One odd reading is not a deploy --
      // offering a reload on it sends the tab straight back to the old bundle.
      // Wait for a second poll to agree before saying anything.
      if (seen !== version) {
        seen = version;
        return;
      }
      if (latest !== version) {
        latest = version;
        dismissed = false;
      }
    }

    check();
    timer = setInterval(check, POLL_MS);
    // Coming back to the tab is the moment it matters most.
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  });
</script>

{#if show}
  <div class="upd" role="status" aria-live="polite">
    <span class="material-symbols-outlined upd-icon">upgrade</span>
    <span class="upd-text">
      A new version is available
      <span class="upd-ver">{latest}</span>
    </span>
    <button type="button" class="upd-reload" onclick={() => location.reload()}>Reload</button>
    <button type="button" class="upd-close" onclick={() => (dismissed = true)} aria-label="Dismiss">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>
{/if}

<style>
  .upd {
    position: fixed;
    inset-block-end: 20px;
    inset-inline-start: 50%;
    transform: translateX(-50%);
    z-index: 1200;
    display: flex;
    align-items: center;
    gap: 10px;
    max-inline-size: calc(100vw - 32px);
    padding: 10px 10px 10px 16px;
    border-radius: 12px;
    background: rgb(var(--md-sys-color-inverse-surface));
    color: rgb(var(--md-sys-color-inverse-on-surface));
    box-shadow: 0 1px 3px rgba(0,0,0,.30), 0 4px 8px 3px rgba(0,0,0,.15);
    font-size: 14px;
  }
  .upd-icon { font-size: 20px; flex-shrink: 0; }
  .upd-text { min-inline-size: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .upd-ver {
    opacity: .75;
    font-family: 'Menlo','Consolas',monospace;
    font-size: 12px;
    margin-inline-start: 6px;
  }
  .upd-reload {
    flex-shrink: 0;
    padding: 6px 16px;
    border: 0;
    border-radius: 999px;
    background: rgb(var(--md-sys-color-inverse-primary));
    color: rgb(var(--md-sys-color-on-primary-container, var(--md-sys-color-on-surface)));
    font: inherit;
    font-weight: 500;
    cursor: pointer;
  }
  .upd-reload:hover { opacity: .9; }
  .upd-reload:focus-visible,
  .upd-close:focus-visible {
    outline: 2px solid rgb(var(--md-sys-color-inverse-primary));
    outline-offset: 2px;
  }
  .upd-close {
    flex-shrink: 0;
    inline-size: 32px;
    block-size: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
  .upd-close:hover { background: rgb(var(--md-sys-color-inverse-on-surface) / 0.12); }
  .upd-close .material-symbols-outlined { font-size: 18px; }
</style>
